import {FileExcelOutlined} from '@ant-design/icons';
import {EmptyFilter, FilterFactory, OperatorFilter, TermFilter} from '@dofleini/query-builder';
import {Button, message} from 'antd';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation} from 'react-query';

import {useFilter} from '@/contexts/FilterContext';
import {useResponsive} from '@/contexts/ResponsiveContext';
import {useTableGrid} from '@/contexts/tableGridContact';
import useAlerts from '@/hooks/useAlerts';
import {getFileName} from '@/utils/utils';

const {EXPORT_STATUS} = require('@mep/pae-commons').nomenclators;

function downloadURI(uri, name) {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // delete link;
}

export const CustomExportComponent = ({
  errorMessage,
  icon,
  params,
  title,
  service,
  filters = new EmptyFilter(),
  translation
}) => {
  let playIntervalId = useRef(null);
  const {t} = useTranslation(translation || 'common');
  const {filter} = useFilter();
  const {isMobile} = useResponsive();
  const [isWorking, setIsWorking] = useState(false);
  const [excel, setExcel] = useState('');
  const {selectedKeys} = useTableGrid();
  
  const existSelection = useMemo(() => !isEmpty(selectedKeys), [selectedKeys]);
  
  const {mutate, isLoading: exportIsLoading} = useMutation(service, {
    onSuccess: ({data}) => {
      if (data) {
        if (data?.status !== EXPORT_STATUS.FAIL) {
          setIsWorking(data?.status === EXPORT_STATUS.WORKING);
          setExcel(data?.document);
        } else {
          setIsWorking(false);
          message.error(t('alert:error.EXPORTERROR.0'));
        }
      }
    },
    onError: (e) => {
      putErrorCode(e || errorMessage);
    }
  });
  const {putErrorCode} = useAlerts();
  
  const handleExport = useCallback(() => {
    mutate({
      ...params,
      filters: existSelection ?
        FilterFactory.add(new OperatorFilter({
          type: 'AND',
          filters: selectedKeys?.map((e) => new TermFilter({field: 'id', value: e}))
        }), filters).toQuery() :
        FilterFactory.add(filters, filter).toQuery()
    });
  }, [mutate, params, existSelection, selectedKeys, filters, filter]);
  
  useEffect(() => {
    if (excel) {
      downloadURI(excel, getFileName(excel));
    }
  }, [excel]);
  
  useEffect(() => {
    if (isWorking) {
      playIntervalId.current = setInterval(() => {
        handleExport();
      }, 10000);
      
      return () => {
        if (playIntervalId?.current) {
          clearInterval(playIntervalId?.current);
          
          // Clear the play interval id.
          playIntervalId.current = null;
        }
      };
    }
  }, [handleExport, isWorking]);
  
  return <div>
    {!isMobile ? (
      <Button icon={icon ? icon : <FileExcelOutlined/>} loading={isWorking || exportIsLoading} onClick={handleExport}>
        {isWorking ? t('working') : t(title)}
      </Button>
    ) : (
      <Button icon={icon ? icon : <FileExcelOutlined/>} loading={isWorking || exportIsLoading} onClick={handleExport}/>
    )}
  </div>;
};

export default memo(CustomExportComponent);

CustomExportComponent.propTypes = {
  documentTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  format: PropTypes.string,
  service: PropTypes.any,
  filters: PropTypes.any,
  translation: PropTypes.string,
  params: PropTypes.any,
  icon: PropTypes.any,
  title: PropTypes.string
};
